/**
 * DC Adjustment Calculator
 * ------------------------
 * This function calculates the spring Drought Code (DC) value based on the overwinter model
 * as described in "Ground-truthing the Drought Code: Field Verification of Overwinter Recharge of
 * Forest Floor Moisture" by Bruce D. Lawson and George N. Dalrymple (November 1996, FRDA report 268).
 * 
 * The original model was developed by Turner and Lawson (1978) and is summarized in Van Wagner (1987).
 * 
 * The function implements the three key equations from the publication:
 * 1) Qs = a * Qr + b * (3.94 * P)
 *    where: Qs = starting spring moisture equivalent of DC value
 *           Qr = final fall moisture equivalent of DC value
 *           P = winter precipitation (mm)
 *           a, b = user-selected values representing carryover fraction and wetting-efficiency fraction
 * 
 * 2) Qr = 800 * exp(-DCf / 400)
 *    where: DCf = final fall DC value
 * 
 * 3) DCs = 400 * ln(800/Qs)
 *    where: DCs = spring DC value
 * 
 * @param {number} fallDC - The final fall Drought Code value (measured October 31 or at freeze-up)
 * @param {number} winterPrecip - Overwinter precipitation in mm between fall and spring
 * @param {number} carryoverFraction - Carryover fraction of fall moisture (usually between 0.0 and 1.0)
 * @param {number} wettingEfficiency - Efficiency of overwinter moisture (usually between 0.0 and 1.0)
 * @returns {number} - The calculated spring Drought Code value
 */
export function calculateSpringDroughtCode(
    fallDC: number,
    winterPrecip: number,
    carryoverFraction: number,
    wettingEfficiency: number
  ): number {
    // Input validation
    if (
      typeof fallDC !== 'number' ||
      typeof winterPrecip !== 'number' ||
      typeof carryoverFraction !== 'number' ||
      typeof wettingEfficiency !== 'number'
    ) {
      throw new Error('All inputs must be numeric values');
    }
  
    if (winterPrecip < 0) {
      throw new Error('Winter precipitation cannot be negative');
    }
  
    if (carryoverFraction < 0 || carryoverFraction > 1) {
      throw new Error('Carryover fraction must be between 0 and 1');
    }
  
    if (wettingEfficiency < 0 || wettingEfficiency > 1) {
      throw new Error('Wetting efficiency must be between 0 and 1');
    }
  
    // Step 1: Calculate Qr - the final fall moisture equivalent from the fall DC value
    // Using equation 2 from Lawson and Dalrymple (1996): Qr = 800 * exp(-DCf / 400)
    const qr: number = 800 * Math.exp(-fallDC / 400);
  
    // Step 2: Calculate Qs - the starting spring moisture equivalent
    // Using equation 1 from Lawson and Dalrymple (1996): Qs = a * Qr + b * (3.94 * P)
    // Where:
    // - a is the carryover fraction (as per Table 1 in the publication)
    // - b is the wetting-efficiency fraction (as per Table 1 in the publication)
    // - 3.94 is the conversion factor for precipitation to DC moisture equivalent
    // - P is the winter precipitation in mm
    const qs: number = carryoverFraction * qr + wettingEfficiency * 3.94 * winterPrecip;
  
    // Step 3: Calculate the spring DC value from Qs
    // Using equation 3 from Lawson and Dalrymple (1996): DCs = 400 * ln(800/Qs)
    let springDC: number = 0;
  
    // Handle edge case: if Qs is 0, we avoid division by zero
    if (qs === 0) {
      springDC = 0;
    } else {
      // Calculate spring DC value
      springDC = 400 * Math.log(800 / qs);
  
      // Apply minimum threshold of 15 for spring DC value as implemented in the Excel calculator
      // This is a practical constraint - very low DC values are not typically used
      if (springDC < 15) {
        springDC = 15;
      }
    }
  
    return springDC;
  }
  
  /**
   * Types for carryover and wetting efficiency categories
   */
  export type CarryoverCategory = 'high' | 'moderate' | 'low';
  export type WettingCategory = 'high' | 'moderate' | 'low';
  
  /**
   * Interface for the return value of getParameterSuggestions
   */
  export interface DroughtCodeParameters {
    carryoverFraction: number;
    wettingEfficiency: number;
  }
  
  /**
   * Function to help users select appropriate values for carryover fraction and wetting efficiency
   * based on Table 1 from Lawson and Dalrymple (1996).
   * 
   * @param {CarryoverCategory} carryoverCategory - 'high', 'moderate', or 'low'
   * @param {WettingCategory} wettingCategory - 'high', 'moderate', or 'low'
   * @returns {DroughtCodeParameters} - An object containing suggested values for carryoverFraction and wettingEfficiency
   */
  export function getParameterSuggestions(
    carryoverCategory: CarryoverCategory,
    wettingCategory: WettingCategory
  ): DroughtCodeParameters {
    // Carryover fraction values from Table 1 in Lawson and Dalrymple (1996)
    const carryoverValues: Record<CarryoverCategory, number> = {
      high: 1.0,      // High fall carryover (wet autumn conditions)
      moderate: 0.75, // Moderate fall carryover (average autumn conditions)
      low: 0.5        // Low fall carryover (dry autumn conditions)
    };
  
    // Wetting efficiency values from Table 1 in Lawson and Dalrymple (1996)
    const wettingValues: Record<WettingCategory, number> = {
      high: 1.0,      // High wetting efficiency (well-drained sites with normal snowmelt)
      moderate: 0.75, // Moderate wetting efficiency (average conditions)
      low: 0.5        // Low wetting efficiency (rapid runoff or high sublimation)
    };
  
    return {
      carryoverFraction: carryoverValues[carryoverCategory],
      wettingEfficiency: wettingValues[wettingCategory]
    };
  }
  
  // Example usage:
  // const params = getParameterSuggestions('moderate', 'high');
  // const springDC = calculateSpringDroughtCode(1000, 100, params.carryoverFraction, params.wettingEfficiency);